/**
 * The public site's 3D art: Blender renders made by the scripts in `art/` at the repo root and encoded into
 * `public/art/`. Each piece is a still (AVIF, with a WebP fallback) and a seamless video loop (WebM, MP4 fallback)
 * whose first frame is the still. The still always shows; the loop fades in over it once it plays. Everything ships
 * from this app, as the CSP requires.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { cx } from '../components/ui';

export type ArtName = 'hero' | 'closing' | 'revenue' | 'spend' | 'close' | 'assistant' | 'developers';

/** Rendered pixel size of each still. The wide ones come in two widths. */
const SIZE: Record<ArtName, [number, number]> = {
  hero: [2400, 1100],
  closing: [2400, 1100],
  revenue: [1200, 1200],
  spend: [1200, 1200],
  close: [1200, 1200],
  assistant: [1200, 1200],
  developers: [1200, 1200],
};
const WIDE = new Set<ArtName>(['hero', 'closing']);
/** Pieces with a square still and loop (`<name>-square`) for phones and portrait tablets, where the wide picture would be a thin strip.
 * Landing.tsx's hero spacing follows the same breakpoints (`sm:landscape:`, `lg:`). */
const SQUARE = new Set<ArtName>(['hero']);
const NARROW = '(max-width: 639px), (max-width: 1023px) and (orientation: portrait)';

/** `controls` puts the pause button in the top or bottom right corner, clear of anything laid over the art; `nav` is the top
 * corner just under the floating nav, for art that runs up behind it. */
type Controls = 'top' | 'bottom' | 'nav';

export function Art({ name, alt = '', sizes, priority, loop = true, controls = 'bottom', className }: { name: ArtName; alt?: string; sizes?: string; priority?: boolean; loop?: boolean; controls?: Controls; className?: string }) {
  const [w, h] = SIZE[name];
  const avif = WIDE.has(name) ? `/art/${name}-1400.avif 1400w, /art/${name}-2400.avif 2400w` : `/art/${name}.avif`;
  return (
    <div className={cx('relative', className)}>
      <picture>
        {SQUARE.has(name) && (
          <>
            <source media={NARROW} type="image/avif" srcSet={`/art/${name}-square.avif`} width={1200} height={1200} />
            <source media={NARROW} type="image/webp" srcSet={`/art/${name}-square.webp`} width={1200} height={1200} />
          </>
        )}
        <source type="image/avif" srcSet={avif} sizes={WIDE.has(name) ? (sizes ?? '100vw') : undefined} />
        <img
          src={`/art/${name}.webp`}
          width={w}
          height={h}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
          decoding="async"
          draggable={false}
          className="block h-auto w-full select-none"
        />
      </picture>
      {loop && <Loop name={name} controls={controls} />}
    </div>
  );
}

/**
 * The moving version, laid exactly over the still. It loads nothing until it scrolls into view, plays only while in
 * view, and stays off when the visitor asks for reduced motion or less data, or has paused the loops. If neither file
 * can play, it removes itself and the still stays.
 */
function Loop({ name, controls }: { name: ArtName; controls: Controls }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const narrow = useMedia(NARROW);
  const on = useSyncExternalStore(motion.subscribe, motion.get, () => true);
  const lessData = useSaveData();
  const allowed = !failed && !reduced && !lessData;
  const file = narrow && SQUARE.has(name) ? `${name}-square` : name;

  useEffect(() => {
    const video = ref.current;
    if (!video || !allowed || !on) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) video.play().catch(() => setPlaying(false));
        else video.pause();
      },
      { threshold: 0.2 },
    );
    io.observe(video);
    return () => {
      io.disconnect();
      video.pause();
    };
  }, [allowed, on, file]);
  // A swapped-in loop fades in once it plays, like the first one.
  useEffect(() => setPlaying(false), [file]);

  if (!allowed) return null;
  return (
    <>
      {/* Keyed by the file, so turning a phone to landscape swaps in the other loop (a video ignores changed sources). */}
      <video
        key={file}
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden
        tabIndex={-1}
        disablePictureInPicture
        onPlaying={() => setPlaying(true)}
        className="site-loop pointer-events-none absolute inset-0 size-full object-cover select-none"
        data-playing={(playing && on) || undefined}
      >
        <source src={`/art/${file}.webm`} type="video/webm" />
        {/* The last source failing means none could play. */}
        <source src={`/art/${file}.mp4`} type="video/mp4" onError={() => setFailed(true)} />
      </video>
      <button
        type="button"
        onClick={() => motion.set(!on)}
        aria-label={on ? 'Pause animations' : 'Play animations'}
        aria-pressed={!on}
        className={cx(
          'absolute right-3 z-[3] grid size-8 place-items-center rounded-full bg-black/35 text-white opacity-60 backdrop-blur-sm transition-opacity hover:opacity-100 focus-visible:opacity-100 sm:right-4',
          { top: 'top-3 sm:top-4', bottom: 'bottom-3 sm:bottom-4', nav: 'top-[72px]' }[controls],
        )}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
          {on ? <path d="M2.5 1.5h2.2v9H2.5zM7.3 1.5h2.2v9H7.3z" /> : <path d="M3 1.4l7.2 4.6L3 10.6z" />}
        </svg>
      </button>
    </>
  );
}

export type IconName = 'revenue' | 'balance' | 'chain' | 'approval' | 'entities' | 'api';

/**
 * A small 3D icon (`art/scenes/icons.py`, encoded by `art/render-icons.sh`): a desk object on a tinted plinth with a
 * soft shadow. It moves as an animated AVIF, a few KB of inter-frame video, so six of them cost less than one loop and
 * need no player. The loop has no alpha (iPhones drew an AVIF sequence's alpha as black squares): it is laid on white and
 * multiplied onto the card, so the white drops out and the shadow darkens what is under it. It shows its still (with
 * alpha, which every browser draws) when the visitor asks for reduced motion or less data, or has paused the site's
 * animations; browsers without AVIF get the still as WebP.
 */
export function ArtIcon({ name, className }: { name: IconName; className?: string }) {
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const on = useSyncExternalStore(motion.subscribe, motion.get, () => true);
  const lessData = useSaveData();
  const moving = on && !reduced && !lessData;
  return (
    // Keyed, so pausing swaps the image at once (a changed <source> alone isn't always picked up).
    <picture key={moving ? 'loop' : 'still'} className={cx('block', className)}>
      <source type="image/avif" srcSet={`/art/icons/${name}-${moving ? 'loop' : 'still'}.avif`} />
      <img src={`/art/icons/${name}.webp`} width={160} height={160} alt="" loading="lazy" decoding="async" draggable={false} className="block size-full mix-blend-multiply select-none" />
    </picture>
  );
}

/** The visitor asked for less data; false on the server and while the browser takes over its HTML (ssr.tsx). */
const useSaveData = () =>
  useSyncExternalStore(
    noChange,
    () => !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData,
    () => false,
  );
const noChange = () => () => {};

function useMedia(query: string) {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(query);
      m.addEventListener('change', cb);
      return () => m.removeEventListener('change', cb);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** One switch for every loop on the site, remembered in this browser: pausing one pauses them all. */
const MOTION = 'raremargin:site-motion';
const listeners = new Set<() => void>();
let fallback = true;
const motion = {
  get: () => {
    try {
      return localStorage.getItem(MOTION) !== 'off';
    } catch {
      return fallback;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) localStorage.removeItem(MOTION);
      else localStorage.setItem(MOTION, 'off');
    } catch {
      // Storage can be off (private mode); the switch then lasts until the page reloads.
      fallback = on;
    }
    listeners.forEach((l) => l());
  },
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => void listeners.delete(l);
  },
};
